<template>
  <div class="flex flex-wrap items-center gap-0.5 border-b border-[#1c4848]/5 bg-white px-2 py-1.5" role="toolbar" aria-label="Formatting">
    <ToolButton label="Undo" shortcut="⌘Z" icon="solar:undo-left-linear" :disabled="!editor.can().undo()" @click="editor.chain().focus().undo().run()" />
    <ToolButton label="Redo" shortcut="⇧⌘Z" icon="solar:undo-right-linear" :disabled="!editor.can().redo()" @click="editor.chain().focus().redo().run()" />
    <span class="mx-0.5 h-5 w-px bg-[#1c4848]/10" aria-hidden="true"></span>

    <select :value="currentStyle" aria-label="Text style" class="h-8 rounded-lg bg-transparent px-1 text-xs font-semibold text-[#1c4848] outline-none hover:bg-[#1c4848]/5" @change="setStyle($event.target.value)">
      <option value="0">Normal text</option>
      <option value="1">Heading 1</option>
      <option value="2">Heading 2</option>
      <option value="3">Heading 3</option>
    </select>
    <span class="mx-0.5 h-5 w-px bg-[#1c4848]/10" aria-hidden="true"></span>

    <ToolButton label="Bold" shortcut="⌘B" icon="solar:text-bold-linear" :active="editor.isActive('bold')" @click="editor.chain().focus().toggleBold().run()" />
    <ToolButton label="Italic" shortcut="⌘I" icon="solar:text-italic-linear" :active="editor.isActive('italic')" @click="editor.chain().focus().toggleItalic().run()" />
    <ToolButton label="Underline" shortcut="⌘U" icon="solar:text-underline-linear" :active="editor.isActive('underline')" @click="editor.chain().focus().toggleUnderline().run()" />
    <ToolButton label="Strikethrough" icon="solar:text-cross-linear" :active="editor.isActive('strike')" @click="editor.chain().focus().toggleStrike().run()" />
    <ToolButton label="Highlight" icon="solar:pen-new-round-linear" :active="editor.isActive('highlight')" @click="editor.chain().focus().toggleHighlight().run()" />
    <div class="relative">
      <ToolButton label="Link" shortcut="⌘K" icon="solar:link-linear" :active="editor.isActive('link')" @click="openLink" />
      <form v-if="linkOpen" class="absolute left-0 top-full z-30 mt-1 flex w-80 items-center gap-1 rounded-xl bg-white p-2 shadow-xl ring-1 ring-[#1c4848]/10" @submit.prevent="applyLink" @keydown.esc="linkOpen = false">
        <input ref="linkInput" v-model="linkUrl" type="url" placeholder="https://" aria-label="Link address" class="min-w-0 flex-1 rounded-lg border border-[#1c4848]/10 px-2 py-1.5 text-xs text-[#1c4848] outline-none focus:border-[#1c4848]/30">
        <button type="submit" class="rounded-lg bg-[#1c4848] px-3 py-1.5 text-xs font-bold text-white">Apply</button>
        <button v-if="editor.isActive('link')" type="button" class="rounded-lg px-2 py-1.5 text-xs font-bold text-red-700 hover:bg-red-50" @click="removeLink">Remove</button>
      </form>
    </div>
    <span class="mx-0.5 h-5 w-px bg-[#1c4848]/10" aria-hidden="true"></span>

    <ToolButton v-for="side in ALIGN" :key="side.value" :label="side.label" :icon="side.icon" :active="editor.isActive({ textAlign: side.value })" @click="editor.chain().focus().setTextAlign(side.value).run()" />
    <span class="mx-0.5 h-5 w-px bg-[#1c4848]/10" aria-hidden="true"></span>

    <ToolButton label="Bulleted list" icon="solar:list-linear" :active="editor.isActive('bulletList')" @click="editor.chain().focus().toggleBulletList().run()" />
    <ToolButton label="Numbered list" icon="solar:list-check-linear" :active="editor.isActive('orderedList')" @click="editor.chain().focus().toggleOrderedList().run()" />
    <ToolButton label="Quote" icon="solar:chat-square-like-linear" :active="editor.isActive('blockquote')" @click="editor.chain().focus().toggleBlockquote().run()" />

    <template v-if="editor.isActive('table')">
      <span class="mx-0.5 h-5 w-px bg-[#1c4848]/10" aria-hidden="true"></span>
      <button v-for="action in TABLE" :key="action.label" type="button" class="h-8 rounded-lg px-2 text-[11px] font-bold text-[#1c4848]/70 hover:bg-[#1c4848]/5 hover:text-[#1c4848]" @click="action.run(editor.chain().focus()).run()">{{ action.label }}</button>
    </template>
  </div>
</template>

<script setup>
import { computed, nextTick, ref } from 'vue';
import ToolButton from '@/components/writer/ToolButton.vue';

const ALIGN = [
  { value: 'left', label: 'Align left', icon: 'solar:align-left-linear' },
  { value: 'center', label: 'Align center', icon: 'solar:align-horizontal-center-linear' },
  { value: 'right', label: 'Align right', icon: 'solar:align-right-linear' },
];
const TABLE = [
  { label: '+ Row', run: (c) => c.addRowAfter() },
  { label: '+ Column', run: (c) => c.addColumnAfter() },
  { label: '− Row', run: (c) => c.deleteRow() },
  { label: '− Column', run: (c) => c.deleteColumn() },
  { label: 'Delete table', run: (c) => c.deleteTable() },
];

const props = defineProps({ editor: { type: Object, required: true } });

const linkOpen = ref(false);
const linkUrl = ref('');
const linkInput = ref(null);

const currentStyle = computed(() => String([1, 2, 3].find((level) => props.editor.isActive('heading', { level })) ?? 0));

function setStyle(level) {
  const chain = props.editor.chain().focus();
  (level === '0' ? chain.setParagraph() : chain.setHeading({ level: Number(level) })).run();
}

function openLink() {
  linkUrl.value = props.editor.getAttributes('link').href ?? '';
  linkOpen.value = !linkOpen.value;
  nextTick(() => linkInput.value?.focus());
}

function applyLink() {
  const url = linkUrl.value.trim();
  if (url) {
    props.editor.chain().focus().extendMarkRange('link').setLink({ href: /^(https?:|mailto:)/i.test(url) ? url : `https://${url}` }).run();
  }
  linkOpen.value = false;
}

function removeLink() {
  props.editor.chain().focus().extendMarkRange('link').unsetLink().run();
  linkOpen.value = false;
}

defineExpose({ openLink });
</script>
